import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { finalize, Observable } from 'rxjs';
import {
  AddIconComponent,
  CloseSquareIconComponent,
  DeleteIconComponent,
  TickSquareIconComponent,
} from '@kuiper/icons';
import { jobTitleList } from '../job-title-model';
import { JobTitleService } from '../job-title.service';
import {
  JOB_TITLE_LIST_COLUMNS_SORT,
  JOB_TITLE_LIST_FILTER_TABS,
  JOB_TITLE_COLUMN_IDENTIFIERS,
  ACTIONS_MENU_ITEMS,
  ACTIONS_INDEXES,
} from './job-title-list.constants';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { FilteredTableComponent } from '../../../../../common/components/filtered-table/filtered-table.component';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { UserStatusPipe } from '../../../../../common/pipes/user-status/user-status.pipe';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { DEFAULT_PAGE_SIZE } from '../../../../../common/constants/pagination.constants';
import {
  injectFloatingLayout,
  provideSystemTabsLayout,
  injectFloatingPageClasses,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import {
  ApiResponse,
  Meta,
} from '../../../../../common/models/api-response.model';
import { ActionList } from '../../../../../common/models/action-list.model';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../../ui-components/lib/table/table.model';
import { FilteredTablePayload } from '../../../../../common/components/filtered-table/filtered-table.model';

@Component({
  selector: 'lib-job-title-list',
  imports: [
    TranslocoPipe,
    FilteredTableComponent,
    BadgeComponent,
    ActionListComponent,
    RouterModule,
    AddIconComponent,
    CloseSquareIconComponent,
    DeleteIconComponent,
    TickSquareIconComponent,
    TenantPathPipe,
    UserStatusPipe,
  ],
  templateUrl: './job-title-list.component.html',
  styleUrl: './job-title-list.component.scss',
  providers: [provideSystemTabsLayout()],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class JobTitleListComponent {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  readonly ADMIN_ROUTES = ADMIN_ROUTES;
  readonly JOB_TITLE_COLUMN_IDENTIFIERS = JOB_TITLE_COLUMN_IDENTIFIERS;

  private readonly transloco = inject(TranslocoService);
  private readonly jobTitleService = inject(JobTitleService);
  private readonly router = inject(Router);
  private readonly tenantService = inject(TenantService);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly modalService = inject(ModalServiceService);
  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  deactivateModal = viewChild<TemplateRef<unknown>>('deactivate');
  activateModal = viewChild<TemplateRef<unknown>>('activate');
  deleteModal = viewChild<TemplateRef<unknown>>('deleteRecord');

  readonly isDeleteInactiveEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT
        .delete_inactive_groups_departments_job_titles_and_positions
    )
  ).asReadonly();

  isLoading = signal<boolean>(false);
  jobTitleList = signal<jobTitleList[]>([]);
  meta = signal<Meta | undefined>(undefined);
  columns = signal<TableColumns[]>(
    JOB_TITLE_LIST_COLUMNS_SORT.map(col => ({
      ...col,
      header: col.header ? this.transloco.translate(col.header) : '',
    }))
  );
  tableFilterTabs = signal<TableFilterTab[]>(
    JOB_TITLE_LIST_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );
  payload = signal<FilteredTablePayload>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    sortColumn: {
      key: '',
      value: 'asc',
    },
    search: '',
    filters: {},
    tab: this.tableFilterTabs()[0],
  });

  selectedJobTitle = signal<jobTitleList | undefined>(undefined);
  isJobTitleStateLoading = signal<boolean>(false);

  actionItemsMapped = signal<ActionList[]>(
    ACTIONS_MENU_ITEMS.map(item => ({
      ...item,
      label: this.transloco.translate(item.label),
    }))
  );

  actionMenuItems = computed(() => {
    const jobTitle = this.selectedJobTitle();
    const actionItemsMapped = this.actionItemsMapped();

    let actionItems: ActionList[] = [];

    if (!jobTitle) {
      return actionItems;
    }

    const viewDetailsAction = {
      ...actionItemsMapped[ACTIONS_INDEXES.VIEW],
      routerLinks: [
        this.tenantService.adminPath(
          `${ADMIN_ROUTES.JOB_TITLES.DETAILS}/${jobTitle.id.toString()}`
        ),
      ],
    };

    const editAction = {
      ...actionItemsMapped[ACTIONS_INDEXES.EDIT],
      hasDivider: true,
      routerLinks: [
        this.tenantService.adminPath(
          `${ADMIN_ROUTES.JOB_TITLES.FORM}/${jobTitle.id.toString()}`
        ),
      ],
    };

    actionItems = [viewDetailsAction, editAction];

    if (jobTitle.state?.value === 'active') {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.DEACTIVATE]);
    } else if (jobTitle.state?.value === 'inactive') {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.ACTIVATE]);
    }

    if (
      this.isDeleteInactiveEnabled() &&
      jobTitle.state?.value === 'inactive'
    ) {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.DELETE]);
    }

    return actionItems;
  });

  getJobTitleList$ = () => {
    this.isLoading.set(true);
    this.meta.set(undefined);

    const { page, size, sortColumn, tab, search } = this.payload();

    return this.jobTitleService
      .getJobTitleList(page, size, sortColumn, tab?.key, search)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      );
  };

  getJobTitleList() {
    this.getJobTitleList$().subscribe({
      next: (res: ApiResponse<jobTitleList>) =>
        this.handleJobTitleListSuccess(res),
    });
  }

  handleJobTitleListSuccess(res: ApiResponse<jobTitleList>) {
    this.jobTitleList.set(res.data);
    this.meta.set(res.meta);
  }

  handleAction($event: string, row: jobTitleList) {
    this.selectedJobTitle.set(row);

    switch ($event) {
      case 'view':
        this.router.navigate([
          this.tenantService.adminPath(`${ADMIN_ROUTES.JOB_TITLES.DETAILS}`),
          row.id,
        ]);
        break;
      case 'edit':
        this.router.navigate([
          this.tenantService.adminPath(`${ADMIN_ROUTES.JOB_TITLES.FORM}`),
          row.id,
        ]);
        break;
      case 'deactivate':
        this.openModal('deactivate');
        break;
      case 'activate':
        this.openModal('activate');
        break;
      case 'delete':
        this.openModal('delete');
        break;
    }
  }

  openModal(type: 'activate' | 'deactivate' | 'delete') {
    const customizeContent =
      type === 'deactivate'
        ? this.deactivateModal()
        : type === 'activate'
          ? this.activateModal()
          : this.deleteModal();

    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: false,
      customizeContent,
      width: '24',
    });
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  deactivateJobTitle() {
    this.updateJobTitleState(jobTitleId =>
      this.jobTitleService.deactivateJobTitle(jobTitleId)
    );
  }

  activateJobTitle() {
    this.updateJobTitleState(jobTitleId =>
      this.jobTitleService.activateJobTitle(jobTitleId)
    );
  }

  deleteJobTitle() {
    this.updateJobTitleState(jobTitleId =>
      this.jobTitleService.deleteJobTitle(jobTitleId)
    );
  }

  updateJobTitleState(request: (jobTitleId: string) => Observable<unknown>) {
    const jobTitleId = this.selectedJobTitle()?.id;
    if (!jobTitleId) {
      return;
    }

    this.isJobTitleStateLoading.set(true);
    request(jobTitleId.toString())
      .pipe(
        finalize(() => this.isJobTitleStateLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.getJobTitleList();
          this.closeModal();
        },
        error: (error: { message?: string }) => {
          this.toaster.add(error.message ?? '', '', '', 'error', true, true);
        },
      });
  }

  viewJobTitleDetails(jobTitle: jobTitleList) {
    this.router.navigateByUrl(
      this.tenantService.adminPath(
        `${ADMIN_ROUTES.JOB_TITLES.DETAILS}/${jobTitle.id}`
      )
    );
  }
}
